import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { parseRoundRule, roundRuleLabel } from '../roundingLabel';

const WD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface Day { dayOff: boolean; start: string; end: string; breakMins: number; }

const defaultDays = (): Day[] =>
  WD.map((_, i) => i < 5
    ? { dayOff: false, start: '09:00', end: '17:00', breakMins: 0 }
    : { dayOff: true, start: '', end: '', breakMins: 0 });

const normDays = (d: any): Day[] =>
  Array.isArray(d) && d.length === 7
    ? d.map((x: any) => ({ dayOff: !!x.dayOff, start: x.start || '', end: x.end || '', breakMins: x.breakMins || 0 }))
    : defaultDays();

const minutesBetween = (s: string, e: string) => {
  if (!s || !e) return 0;
  const [sh, sm] = s.split(':').map(Number);
  const [eh, em] = e.split(':').map(Number);
  return Math.max(0, (eh * 60 + em) - (sh * 60 + sm));
};
const totalMinutes = (days: Day[]) =>
  days.reduce((sum, d) => sum + (d.dayOff ? 0 : Math.max(0, minutesBetween(d.start, d.end) - (d.breakMins || 0))), 0);
const fmtH = (mins: number) => `${Math.round((mins / 60) * 10) / 10}`;

const HrWorkingPatterns: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [list, setList] = useState<any[]>([]);
  const [usage, setUsage] = useState<Record<number, number>>({});
  const [usageShownId, setUsageShownId] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [entitlementUnit, setEntitlementUnit] = useState<'days' | 'hours'>('days');
  const [holidayPolicy, setHolidayPolicy] = useState<'deducted' | 'not_deducted' | 'works'>('works');
  const [useToil, setUseToil] = useState(true);
  const [useRounding, setUseRounding] = useState(true);
  const [roundingCfg, setRoundingCfg] = useState<{ day: string; hour: string }>({ day: 'up', hour: 'up' });
  const [days, setDays] = useState<Day[]>(defaultDays());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [listRes, usageRes, coRes] = await Promise.allSettled([
        axios.get('hr-schedule-template'),
        axios.get('hr-schedule-template/usage'),
        axios.get('user'),
      ]);
      if (listRes.status === 'fulfilled') setList(Array.isArray(listRes.value.data) ? listRes.value.data : []);
      if (coRes.status === 'fulfilled') {
        const c = coRes.value.data?.company || {};
        const legacy: Record<string, string> = { full_up: 'up', half_up: 'up_half', nearest_half: 'nearest_half', tenth: 'none', none: 'none' };
        setRoundingCfg({ day: c.hr_rounding_day ?? legacy[c.hr_rounding] ?? 'up', hour: c.hr_rounding_hour ?? 'up' });
      }
      if (usageRes.status === 'fulfilled' && Array.isArray(usageRes.value.data)) {
        const map: Record<number, number> = {};
        usageRes.value.data.forEach((r: any) => { map[r.templateId] = Number(r.count); });
        setUsage(map);
      }
    } catch {
      // глобальный interceptor
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setDay = (i: number, patch: Partial<Day>) =>
    setDays(p => p.map((d, idx) => idx === i ? { ...d, ...patch } : d));

  const loadForm = (tpl: any) => {
    setName(tpl.name || '');
    setIsDefault(!!tpl.isDefault);
    setEntitlementUnit(tpl.entitlement_unit === 'hours' ? 'hours' : 'days');
    setHolidayPolicy(tpl.public_holiday_policy || (tpl.works_public_holidays ? 'works' : 'deducted'));
    setUseToil(tpl.use_toil ?? true);
    setUseRounding(tpl.use_rounding ?? true);
    setDays(normDays(tpl.days));
  };

  const toggleRow = (tpl: any) => {
    if (expanded === tpl.id) { setExpanded(null); setConfirmDelete(false); return; }
    setAddingNew(false);
    loadForm(tpl); setConfirmDelete(false); setExpanded(tpl.id);
  };

  const openNew = () => {
    setExpanded(null); setConfirmDelete(false);
    setName(''); setIsDefault(false); setEntitlementUnit('days'); setHolidayPolicy('works'); setUseToil(true); setUseRounding(true); setDays(defaultDays());
    setAddingNew(true);
  };

  const buildPayload = (nm: string, def: boolean) => {
    const tm = totalMinutes(days);
    return {
      name: nm.trim(), isDefault: def,
      works_public_holidays: holidayPolicy === 'works',
      public_holiday_policy: holidayPolicy,
      entitlement_unit: entitlementUnit,
      use_toil: useToil,
      use_rounding: useRounding,
      days, total_minutes: tm, hoursPerWeek: tm / 60,
    };
  };

  const persist = async (id: number | null, nm = name, def = isDefault) => {
    if (!nm.trim()) { enqueueSnackbar(t('FillRequired') as string, { variant: 'error' }); return; }
    setSaving(true);
    try {
      if (id) await axios.put(`hr-schedule-template/${id}`, buildPayload(nm, def));
      else await axios.post('hr-schedule-template/add', buildPayload(nm, def));
      enqueueSnackbar(t('Saved') as string, { variant: 'success' });
      setAddingNew(false); setExpanded(null); load();
    } catch {
      enqueueSnackbar(t('Error') as string, { variant: 'error' });
    } finally { setSaving(false); }
  };

  const duplicate = () => persist(null, `${name.trim()} (copy)`, false);

  const remove = async (id: number) => {
    try {
      await axios.delete(`hr-schedule-template/${id}`);
      enqueueSnackbar(t('Saved') as string, { variant: 'success' });
      setExpanded(null); setConfirmDelete(false); load();
    } catch { enqueueSnackbar(t('Error') as string, { variant: 'error' }); }
  };

  const ic = 'timesheet-edit-input';

  const dayEditor = (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead><tr>{['', t('HrWorking'), t('From'), t('To'), `${t('HrBreak')} (${t('Min')})`].map((h, i) => (
        <th key={i} style={{ textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-color-calendar-week)', padding: '4px 6px' }}>{h}</th>
      ))}</tr></thead>
      <tbody>
        {WD.map((d, i) => (
          <tr key={d}>
            <td style={{ padding: '4px 6px', color: 'var(--text-color)' }}>{t('WD_' + d)}</td>
            <td style={{ padding: '4px 6px' }}>
              <input type="checkbox" title={d} checked={!days[i].dayOff} onChange={e => setDay(i, { dayOff: !e.target.checked })} />
            </td>
            {days[i].dayOff ? (
              <td colSpan={3} style={{ padding: '4px 6px', color: 'var(--text-color-calendar-week)', fontSize: 12 }}>— {t('HrDayOff')} —</td>
            ) : (
              <>
                <td style={{ padding: '4px 6px' }}><input type="time" title="start" className={ic} value={days[i].start} onChange={e => setDay(i, { start: e.target.value })} style={{ padding: '4px 6px' }} /></td>
                <td style={{ padding: '4px 6px' }}><input type="time" title="end" className={ic} value={days[i].end} onChange={e => setDay(i, { end: e.target.value })} style={{ padding: '4px 6px' }} /></td>
                <td style={{ padding: '4px 6px' }}><input type="number" title="break" className={ic} value={days[i].breakMins} onChange={e => setDay(i, { breakMins: Number(e.target.value) })} style={{ padding: '4px 6px', width: 70 }} /></td>
              </>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );

  const summaryLine = (
    <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>{t('HrPerWeek')}: <b style={{ color: 'var(--text-color)' }}>{fmtH(totalMinutes(days))}{t('HShort')}</b> ({t('HrExclBreaks')})</div>
  );

  const PH_OPTS: { val: 'deducted' | 'not_deducted' | 'works'; title: string; desc: string }[] = [
    { val: 'deducted', title: t('HrPhDeducted') as string, desc: t('HrPhDeductedDesc') as string },
    { val: 'not_deducted', title: t('HrPhNotDeducted') as string, desc: t('HrPhNotDeductedDesc') as string },
    { val: 'works', title: t('HrPhWorks') as string, desc: t('HrPhWorksDesc') as string },
  ];

  const settingsBlock = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-l2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-color-calendar-week)' }}>{t('HrLeaveUnit')}</span>
        <button type="button" className={`dt-btn-ghost${entitlementUnit === 'days' ? ' active' : ''}`} onClick={() => setEntitlementUnit('days')}>{t('HrUnitDays')}</button>
        <button type="button" className={`dt-btn-ghost${entitlementUnit === 'hours' ? ' active' : ''}`} onClick={() => setEntitlementUnit('hours')}>{t('HrUnitHours')}</button>
      </div>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text-color)', cursor: 'pointer' }}>
        <input type="checkbox" checked={useToil} onChange={e => setUseToil(e.target.checked)} style={{ marginTop: 2 }} />
        <span>
          <div style={{ fontWeight: 600 }}>{t('HrUseToil')}</div>
          <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>{t('HrUseToilDesc')}</div>
        </span>
      </label>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text-color)', cursor: 'pointer' }}>
        <input type="checkbox" checked={useRounding} onChange={e => setUseRounding(e.target.checked)} style={{ marginTop: 2 }} />
        <span>
          <div style={{ fontWeight: 600 }}>{t('HrUseRounding', 'Round leave entitlement')}</div>
          <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>
            {useRounding
              ? (() => {
                  const rule = entitlementUnit === 'hours' ? roundingCfg.hour : roundingCfg.day;
                  const unitWord = entitlementUnit === 'hours' ? t('HrUnitHour', 'hour') : t('HrUnitDay', 'day');
                  const { step, thr, none } = parseRoundRule(rule);
                  if (none) return t('HrRoundingNoneCfg', 'No rounding rule set in HR settings — value stays exact.');
                  return `${t('HrRoundingDescPrefix', 'Entitlement is rounded')} ${roundRuleLabel(step, thr, unitWord, t)} (${t('HrFromSettings', 'rule from HR settings')}).`;
                })()
              : t('HrRoundingOffDesc', 'Entitlement is shown exactly, without rounding.')}
          </div>
        </span>
      </label>

      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-color)', marginBottom: 4 }}>{t('HrPublicHolidays')}</div>
        <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)', marginBottom: 6 }}>{t('HrPhIntro')}</div>
        {PH_OPTS.map(o => (
          <label key={o.val} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '7px 0', cursor: 'pointer' }}>
            <input type="radio" checked={holidayPolicy === o.val} onChange={() => setHolidayPolicy(o.val)} style={{ marginTop: 2 }} />
            <span>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-color)' }}>{o.title}</div>
              <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>{o.desc}</div>
            </span>
          </label>
        ))}
      </div>
    </div>
  );

  const expandedContent = (id: number | null) => (
    <div style={{ padding: '0 var(--space-l2) var(--space-l2)', display: 'flex', flexDirection: 'column', gap: 'var(--space-l2)' }}>
      {/* Название паттерна */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-color-calendar-week)' }}>{t('HrPatternName')}</label>
        <input
          className={`${ic} w-full`}
          value={name}
          onChange={e => setName(e.target.value)}
          style={{ maxWidth: 320 }}
          autoFocus={id === null}
        />
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: 'var(--text-color)', cursor: 'pointer' }}>
        <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} /> {t('HrSetAsDefault')}
      </label>
      {dayEditor}
      {summaryLine}
      {settingsBlock}

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)', marginTop: 'var(--space-l1)' }}>
        {id !== null && (
          !confirmDelete ? (
            <>
              <button type="button" className="dt-btn-ghost" style={{ color: 'var(--color-danger)' }} onClick={() => setConfirmDelete(true)}>🗑 {t('HrDelete')}</button>
              <button type="button" className="dt-btn-ghost" onClick={duplicate} disabled={saving}>⧉ {t('HrDuplicate')}</button>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)' }}>
              <span style={{ fontSize: 12, color: 'var(--color-danger)', fontWeight: 600 }}>{t('HrConfirmDelete')}</span>
              <button type="button" className="btn-primary" style={{ background: 'var(--color-danger)' }} onClick={() => remove(id)}>{t('HrDelete')}</button>
              <button type="button" className="dt-btn-ghost" onClick={() => setConfirmDelete(false)}>{t('Cancel')}</button>
            </div>
          )
        )}
        <div style={{ flex: 1 }} />
        <button type="button" className="dt-btn-ghost" onClick={() => { setExpanded(null); setAddingNew(false); setConfirmDelete(false); }}>{t('Cancel')}</button>
        <button type="button" className="btn-primary" onClick={() => persist(id)} disabled={saving}>{t('Save')}</button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-l2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)' }}>
        <div className="section-label" style={{ margin: 0 }}><span className="tr-section-dot" style={{ background: '#7c3aed' }} />{t('HrWorkingPatterns')}</div>
        <div style={{ flex: 1 }} />
        {!addingNew && (
          <button type="button" className="btn-primary" onClick={openNew}>+ {t('HrAddPattern')}</button>
        )}
      </div>

      {/* Новый паттерн — всегда сверху списка */}
      {addingNew && (
        <div style={{ background: 'var(--bg-form)', border: '1px solid var(--color-accent, #7c3aed)', borderRadius: 'var(--radius-card)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)', padding: 'var(--space-l2)' }}>
            <span style={{ fontWeight: 600, color: 'var(--text-color-calendar-week)', fontSize: 12 }}>{t('HrAddPattern')}</span>
          </div>
          {expandedContent(null)}
        </div>
      )}

      {list.length === 0 && !addingNew ? (
        <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>{t('Empty')}</div>
      ) : list.map(tpl => {
        const tm = tpl.total_minutes ?? totalMinutes(normDays(tpl.days));
        const open = expanded === tpl.id;
        return (
          <div key={tpl.id} style={{ background: 'var(--bg-form)', border: 'var(--border-input)', borderRadius: 'var(--radius-card)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)', padding: 'var(--space-l2)', cursor: 'pointer' }}
              onClick={() => toggleRow(tpl)}>
              <span style={{ fontWeight: 700, color: 'var(--text-color)', fontSize: 13 }}>{open ? name : tpl.name}</span>
              {tpl.isDefault && <span className="sbadge fulfilled">{t('Default')}</span>}
              <span style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>{fmtH(tm)}{t('HShort')}/{t('HrPerWeek')}</span>
              <div style={{ flex: 1 }} />
              <span className="btn-icon" style={{ pointerEvents: 'none' }}>{open ? '▴' : '▾'}</span>
              <button type="button" className="btn-icon" title={t('HrUsersUsing') as string}
                onClick={(e) => { e.stopPropagation(); setUsageShownId(usageShownId === tpl.id ? null : tpl.id); }}>
                <FontAwesomeIcon icon={faUser} />
              </button>
              {usageShownId === tpl.id && (
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-color)' }}>
                  {usage[tpl.id] || 0} <span style={{ fontWeight: 400, color: 'var(--text-color-calendar-week)' }}>{t('HrUsersUsing')}</span>
                </span>
              )}
            </div>

            {open && expandedContent(tpl.id)}
          </div>
        );
      })}
    </div>
  );
};

export default HrWorkingPatterns;
